import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { useNavigate } from '@tanstack/react-router'
import debounce from 'lodash/debounce'
import { NoResultsFound } from 'ui/temp'

import { ResultsListProps } from './ResultsList.types'
import { ResultItem } from './components'
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation'
import { getNavigateOptions } from './utils/getUrl'

import { s } from './styles'

export const ResultsList: FC<ResultsListProps> = ({ searchText, results, hasQueried, onClose }) => {
  const navigate = useNavigate()

  const [isHoverDisabled, setIsHoverDisabled] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null)
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null)

  const [activeItem, setActiveItem] = useState<{ sectionIndex: number; itemIndex: number } | null>(null)

  useEffect(() => {
    if (results && results.length > 0) {
      setActiveItem({
        sectionIndex: 0,
        itemIndex: 0
      })
    }
  }, [results])

  const disableHover = useCallback(() => {
    setIsHoverDisabled(true)

    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
    }

    hoverTimerRef.current = setTimeout(() => {
      setIsHoverDisabled(false)
    }, 1000)
  }, [])

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current)
      }
    }
  }, [])

  const handleItemSelect = useCallback(() => {
    if (activeItem !== null) {
      navigate({
        ...getNavigateOptions(
          results[activeItem.sectionIndex].items[activeItem.itemIndex].id,
          results[activeItem.sectionIndex].type
        ),
        replace: true
      })
      onClose()
    }
  }, [activeItem, navigate, onClose])

  const handleContainerKeyDown = useKeyboardNavigation(results, handleItemSelect, setActiveItem, disableHover)

  const handleScroll = debounce(() => {
    if (!isHoverDisabled) {
      setActiveItem(null)
      disableHover()
    }
  }, 400)

  useEffect(() => {
    window.addEventListener('keydown', handleContainerKeyDown)
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('keydown', handleContainerKeyDown)
      window.removeEventListener('scroll', handleScroll)
      handleScroll.cancel()
    }
  }, [handleContainerKeyDown, handleScroll])

  useEffect(() => {
    if (activeItem !== null) {
      const timeoutId = setTimeout(() => {
        const activeElement = document.querySelector('.result-item.active')
        if (activeElement && resultsRef.current) {
          const rect = activeElement.getBoundingClientRect()
          const containerRect = resultsRef.current.getBoundingClientRect()

          const isVisible = rect.top >= containerRect.top && rect.bottom <= containerRect.bottom

          if (!isVisible) {
            activeElement.scrollIntoView({
              block: 'start'
            })
          }
        }
      }, 0)

      return () => clearTimeout(timeoutId)
    }

    return undefined
  }, [activeItem])

  return (
    <s.Container
      role="list"
      aria-label="Search Results"
      ref={resultsRef}
      className={isHoverDisabled ? 'hoverDisabled' : ''}
    >
      {hasQueried && (Object.keys(results || {}).length === 0 || searchText.length === 0) && (
        <NoResultsFound dataTestid="no-search-results" subTitle="Try searching for a different term." />
      )}

      {!results || !hasQueried
        ? null
        : results.map((section, sectionIndex) => (
            <s.SectionWrapper key={section.type}>
              <s.SectionTitle>{section.label}</s.SectionTitle>
              <s.ResultListWrapper>
                {section?.items?.map((item, itemIndex) => (
                  <ResultItem
                    key={item.id}
                    item={item}
                    searchText={searchText}
                    isActive={Boolean(
                      activeItem && activeItem.sectionIndex === sectionIndex && activeItem.itemIndex === itemIndex
                    )}
                    tabIndex={0}
                    onClick={handleItemSelect}
                    setIsActive={() => {
                      if (!isHoverDisabled) {
                        setActiveItem({
                          sectionIndex,
                          itemIndex
                        })
                      }
                    }}
                  />
                ))}
              </s.ResultListWrapper>
            </s.SectionWrapper>
          ))}
    </s.Container>
  )
}
